'use client';

import { useAuthProps } from '@tuic/auth/client';
import { logger } from '@tuic/logger';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

import { onLocal } from '../../helpers/env';
import { useTealium } from '../../hooks/use-tealium';
import urls from '../../urls';

import { createOnClickHandler } from './on-click-handler';

import type { TealiumViewDataObject } from '../../tracking/tealium';
import type { AuthProps } from '@tuic/auth';

type ShouldTrackCallback = (_path: string) => boolean;

type Props = {
  /**
   * Set to false to disable tracking for current page.
   */
  shouldTrackPage?: boolean | ShouldTrackCallback;

  /**
   * A functions which returns the tracking props which will be sent as part of the page view.
   */
  getTrackingProps: () => TealiumViewDataObject;
};

/**
 * Returns the tracking attributes corresponding to given user data.
 */
const getTrackableUserAttributes = (auth: Partial<AuthProps> | undefined) => {
  if (auth?.authenticated) {
    return {
      authenticated_user_id: auth.id,
    };
  }

  return {};
};

const isDisabled = (
  shouldTrack: boolean | ShouldTrackCallback,
  pathname: string | null,
) => {
  if (typeof shouldTrack === 'boolean') {
    return !shouldTrack;
  }

  return !shouldTrack(pathname ?? '');
};

export const TealiumTracker = ({
  shouldTrackPage = true,
  getTrackingProps,
}: Props) => {
  const [trackerIsReady, setTrackerIsReady] = useState(false);
  const auth = useAuthProps();
  const { trackView } = useTealium();
  const pathname = usePathname();
  const previousPathname = useRef<string | null>(null);

  useEffect(() => {
    if (
      !auth.ready ||
      !trackerIsReady ||
      pathname === previousPathname.current
    ) {
      return;
    }

    previousPathname.current = pathname;

    if (isDisabled(shouldTrackPage, pathname)) {
      onLocal(() => logger.info('Page view tracking disabled for this page.'));
      return;
    }

    trackView({
      ...getTrackingProps(),
      ...getTrackableUserAttributes(auth),
    });
  }, [shouldTrackPage, pathname, trackerIsReady, auth.ready]);

  useEffect(() => {
    if (isDisabled(shouldTrackPage, pathname)) {
      return;
    }

    const handleClick = createOnClickHandler();

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [shouldTrackPage, pathname]);

  return (
    <>
      <Script id="tealium-spa-setup">{`
        window.utag_cfg_ovrd = window.utag_cfg_ovrd || {};
        window.utag_cfg_ovrd.noview = true;
      `}</Script>
      <Script
        src={urls.tealiumScriptUrl}
        strategy="afterInteractive"
        async
        onLoad={() => setTrackerIsReady(true)}
      />
    </>
  );
};
